import { z } from "zod";
import {
  COLLECTION_DESCRIPTION_MAX,
  MEMBER_IDS_DUPLICATE_MESSAGE,
  MEMBER_IDS_EMPTY_MESSAGE,
  MEMBER_IDS_TOO_MANY_MESSAGE,
  SCHEDULE_MEMBER_IDS_MAX,
} from "./collection-schedules.js";

/**
 * Contract S02 (US-E16.F1-0182 · @UC9231–@UC9234) — DTO create-collection với `amountMode` + union
 * payload 4 mode nâng cao (per_member/tiered/target_split/open_amount) + equal (cũ, không regress).
 *
 * Một nguồn sự thật api (validate biên + shape) ↔ mobile (gọi + map) — DRY.
 *
 * BẢO MẬT / RANH GIỚI (invariants):
 *  - B1 tiền BigInt: MỌI amount = STRING số nguyên (regex `^\d+$`), server parse → BigInt. KHÔNG float.
 *  - C5 discriminated-union theo `amountMode`, MỖI nhánh `.strict()` — field của mode khác (vd
 *    targetTotalMinor trong per_member) bị reject (chống over-post + nhầm payload).
 *  - currency/fundId/kind/status CỐ Ý VẮNG (kế thừa fund / server set).
 *  - response `remainderBearerMemberId` (target_split — đồng lẻ có tên).
 */

/** 5 chế độ thu — GIÁ TRỊ đồng bộ với @goppy/domain COLLECTION_AMOUNT_MODES + CHECK DB (drift-guard). */
export const COLLECTION_AMOUNT_MODE = {
  EQUAL: "equal",
  PER_MEMBER: "per_member",
  TIERED: "tiered",
  TARGET_SPLIT: "target_split",
  OPEN_AMOUNT: "open_amount",
} as const;

export type CollectionAmountMode =
  (typeof COLLECTION_AMOUNT_MODE)[keyof typeof COLLECTION_AMOUNT_MODE];

/** Số chữ số tối đa cho amount (mirror ADHOC_AMOUNT_MAX_DIGITS — trần PG BIGINT). */
const AMOUNT_MODE_MAX_DIGITS = 19;
/** Trần PG signed BIGINT (chống overflow P2020→500). */
const AMOUNT_MODE_PG_BIGINT_MAX = 9223372036854775807n;

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const INTEGER_STRING_PATTERN = /^\d+$/;

const AMOUNT_MODE_TITLE_MAX = 100;
/** Trần số entry/tier/member (chống payload khổng lồ). */
const MAX_ENTRIES = 1000;
const MAX_TIERS = 50;
const TIER_LABEL_MAX = 60;

export const AMOUNT_MODE_INVALID_MESSAGE =
  "Số tiền phải là số nguyên (đơn vị nhỏ nhất)";

/** Số tiền STRING minor dương ≤ trần BIGINT (B1 — reject float/overflow). */
const amountMinorString = z
  .string()
  .trim()
  .min(1, AMOUNT_MODE_INVALID_MESSAGE)
  .max(AMOUNT_MODE_MAX_DIGITS, AMOUNT_MODE_INVALID_MESSAGE)
  .regex(INTEGER_STRING_PATTERN, AMOUNT_MODE_INVALID_MESSAGE)
  .refine((v) => {
    try {
      return BigInt(v) <= AMOUNT_MODE_PG_BIGINT_MAX && BigInt(v) > 0n;
    } catch {
      return false;
    }
  }, AMOUNT_MODE_INVALID_MESSAGE);

function isRealCalendarDate(value: string): boolean {
  const [y, m, d] = value.split("-").map((p) => Number.parseInt(p, 10));
  if (y === undefined || m === undefined || d === undefined) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

const titleField = z.string().trim().min(1).max(AMOUNT_MODE_TITLE_MAX);
const dueDateField = z
  .string()
  .trim()
  .regex(ISO_DATE_PATTERN)
  .refine(isRealCalendarDate);

const memberIdField = z.string().uuid();

/** 1 entry per_member. */
const perMemberEntrySchema = z
  .object({ groupMemberId: memberIdField, amountMinor: amountMinorString })
  .strict();

/** 1 bậc tiered. */
const tierSchema = z
  .object({
    label: z.string().trim().min(1).max(TIER_LABEL_MAX),
    amountMinor: amountMinorString,
  })
  .strict();

/**
 * Superset object `.strict()` cho create-collection theo mode. `amountMode` default 'equal' (body
 * KHÔNG set ⇒ equal, không regress). MỖI field mode-specific OPTIONAL; `.superRefine` chốt bắt buộc
 * đúng mode + reject field mode khác (chống nhầm payload — vd targetTotalMinor trong per_member).
 *
 * Vì là object đơn (KHÔNG union), tương thích `createZodDto` (nestjs-zod). `.strict()` reject field lạ.
 */
export const createCollectionWithModeRequestSchema = z
  .object({
    title: titleField,
    amountMode: z
      .enum([
        COLLECTION_AMOUNT_MODE.EQUAL,
        COLLECTION_AMOUNT_MODE.PER_MEMBER,
        COLLECTION_AMOUNT_MODE.TIERED,
        COLLECTION_AMOUNT_MODE.TARGET_SPLIT,
        COLLECTION_AMOUNT_MODE.OPEN_AMOUNT,
      ])
      .default(COLLECTION_AMOUNT_MODE.EQUAL),
    dueDate: dueDateField,
    // equal:
    amountMinor: amountMinorString.optional(),
    // per_member:
    entries: z.array(perMemberEntrySchema).min(1).max(MAX_ENTRIES).optional(),
    // tiered:
    tiers: z.array(tierSchema).min(1).max(MAX_TIERS).optional(),
    memberTierMap: z
      .record(memberIdField, z.string().trim().min(1).max(TIER_LABEL_MAX))
      .optional(),
    // target_split:
    targetTotalMinor: amountMinorString.optional(),
    /**
     * US-E16.F1-1023 (@UC0464 · RECEIPT-SPLIT S4) — tổng HÓA ĐƠN khi tạo đợt từ màn «Chia tiền». CHỈ hợp lệ ở `per_member` (số từng người
     * lấy từ xem trước). Lưu để thành viên thấy chênh lệch với tổng các khoản (QĐ 22/10: lệch vẫn tạo được). Không phải nghĩa vụ, không vào
     * sổ cái. Có trường này ⇒ người trong `entries` phải thuộc đúng diện phải đóng — từ chối, KHÔNG lọc im (review S3).
     */
    billTotalMinor: amountMinorString.optional(),
    /** Mô tả khoản thu (cùng trần với `POST /collections` thường). Vắng ⇒ NULL. */
    description: z.string().trim().max(COLLECTION_DESCRIPTION_MAX).optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    /** Field CHỈ hợp lệ ở đúng mode; hiện diện ở mode khác ⇒ reject (chống nhầm/over-post). */
    const requireField = (present: boolean, path: string, mode: string) => {
      if (!present) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [path],
          message: `${path} bắt buộc với amountMode='${mode}'`,
        });
      }
    };
    const forbidField = (present: boolean, path: string) => {
      if (present) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [path],
          message: `${path} không hợp lệ với amountMode='${val.amountMode}'`,
        });
      }
    };

    const hasAmount = val.amountMinor !== undefined;
    const hasEntries = val.entries !== undefined;
    const hasTiers = val.tiers !== undefined || val.memberTierMap !== undefined;
    const hasTarget = val.targetTotalMinor !== undefined;
    if (val.billTotalMinor !== undefined && val.amountMode !== COLLECTION_AMOUNT_MODE.PER_MEMBER) {
      forbidField(true, "billTotalMinor");
    }
    // Đợt theo hóa đơn: mỗi người MỘT dòng (so không phân biệt hoa/thường) — trùng là đòi tiền một người hai lần trên cùng hóa đơn.
    if (val.billTotalMinor !== undefined && val.entries) {
      const ids = val.entries.map((e) => e.groupMemberId.toLowerCase());
      if (new Set(ids).size !== ids.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["entries"], message: MEMBER_IDS_DUPLICATE_MESSAGE });
      }
    }

    switch (val.amountMode) {
      case COLLECTION_AMOUNT_MODE.EQUAL:
        requireField(hasAmount, "amountMinor", "equal");
        forbidField(hasEntries, "entries");
        forbidField(val.tiers !== undefined, "tiers");
        forbidField(val.memberTierMap !== undefined, "memberTierMap");
        forbidField(hasTarget, "targetTotalMinor");
        break;
      case COLLECTION_AMOUNT_MODE.PER_MEMBER:
        requireField(hasEntries, "entries", "per_member");
        forbidField(hasAmount, "amountMinor");
        forbidField(val.tiers !== undefined, "tiers");
        forbidField(val.memberTierMap !== undefined, "memberTierMap");
        forbidField(hasTarget, "targetTotalMinor");
        break;
      case COLLECTION_AMOUNT_MODE.TIERED:
        requireField(val.tiers !== undefined, "tiers", "tiered");
        requireField(val.memberTierMap !== undefined, "memberTierMap", "tiered");
        forbidField(hasAmount, "amountMinor");
        forbidField(hasEntries, "entries");
        forbidField(hasTarget, "targetTotalMinor");
        break;
      case COLLECTION_AMOUNT_MODE.TARGET_SPLIT:
        requireField(hasTarget, "targetTotalMinor", "target_split");
        forbidField(hasAmount, "amountMinor");
        forbidField(hasEntries, "entries");
        forbidField(hasTiers, "tiers");
        break;
      case COLLECTION_AMOUNT_MODE.OPEN_AMOUNT:
        forbidField(hasAmount, "amountMinor");
        forbidField(hasEntries, "entries");
        forbidField(hasTiers, "tiers");
        forbidField(hasTarget, "targetTotalMinor");
        break;
    }
  });

export type CreateCollectionWithModeRequest = z.infer<
  typeof createCollectionWithModeRequestSchema
>;

/**
 * Shape response 201 cho create-collection theo mode. amount trả STRING (B1). remainderBearerMemberId
 * chỉ có ở target_split (đồng lẻ có tên) — null các mode khác.
 */
export const createCollectionWithModeResponseSchema = z
  .object({
    collectionId: z.string().uuid(),
    fundId: z.string().uuid(),
    title: z.string(),
    amountMode: z.enum([
      COLLECTION_AMOUNT_MODE.EQUAL,
      COLLECTION_AMOUNT_MODE.PER_MEMBER,
      COLLECTION_AMOUNT_MODE.TIERED,
      COLLECTION_AMOUNT_MODE.TARGET_SPLIT,
      COLLECTION_AMOUNT_MODE.OPEN_AMOUNT,
    ]),
    currency: z.string().length(3),
    dueDate: z.string().regex(ISO_DATE_PATTERN),
    duesCreated: z.number().int().nonnegative(),
    /** target_split: member gánh đồng lẻ; các mode khác = null. */
    remainderBearerMemberId: z.string().uuid().nullable(),
    /** @UC0464 — tổng hóa đơn của đợt tạo theo hóa đơn; null với đợt thường. */
    billTotalMinor: amountMinorString.nullable(),
  })
  .strict();

export type CreateCollectionWithModeResponse = z.infer<
  typeof createCollectionWithModeResponseSchema
>;

/* ───────────────────────── xem trước cách chia (RECEIPT-SPLIT S3) ───────────────────────── */

/**
 * US-E16.F1-1022 (@UC0463) — `POST /v1/funds/:id/collections/split-preview` (QĐ người dùng 22/10/2026): tạo đợt thu một lần THEO HÓA ĐƠN.
 * ĐỌC THUẦN — không tạo đợt, không ghi gì. Hai cách gọi:
 *  - không `amounts` ⇒ server CHIA ĐỀU `totalMinor` cho `memberIds` (bộ chia `splitTargetTotal`, Σ = tổng không lệch 1đ, đồng lẻ có tên);
 *  - có `amounts` (người dùng đã sửa từng người) ⇒ server KHÔNG chia lại, chỉ cộng và báo chênh lệch với tổng hóa đơn (khớp / thiếu / vượt).
 * App không tự cộng trừ tiền (CLAUDE.md §3 · cổng B8) — mọi con số trên màn «Chia tiền» đến từ đây.
 * `amounts` phải phủ ĐÚNG những người trong `memberIds`, mỗi người một lần: thiếu người là đòi server đoán số cho người đó.
 */
/** Mã lỗi ổn định của xem trước cách chia (client map `code → câu`). */
export const SPLIT_PREVIEW_ERROR_CODES = Object.freeze({
  /**
   * 422 — người được chọn không thuộc DIỆN PHẢI ĐÓNG (`buildObligationMemberWhere`), vd ghế người lớn `self` ở nhóm phụ huynh. Khác
   * `COLLECTION_MEMBER_NOT_ACTIVE` (đã rời nhóm): đây là ghế đang ở nhóm nhưng không bao giờ gánh khoản thu. Xem trước KHÔNG chia cho ghế mà
   * đường tạo thật (`per_member`) sẽ bỏ — nếu không, xem trước nói «khớp» mà đợt thu ghi thiếu (review S3).
   */
  MEMBER_NOT_OBLIGATED: "COLLECTION_MEMBER_NOT_OBLIGATED",
  /** 422 — chia đều mà tổng nhỏ hơn số người ⇒ có suất 0 đồng (đường tạo thật từ chối khoản 0). */
  TOTAL_TOO_SMALL: "SPLIT_TOTAL_TOO_SMALL",
} as const);

export const splitPreviewRequestSchema = z
  .object({
    totalMinor: amountMinorString,
    memberIds: z
      .array(memberIdField)
      .min(1, MEMBER_IDS_EMPTY_MESSAGE)
      .max(SCHEDULE_MEMBER_IDS_MAX, MEMBER_IDS_TOO_MANY_MESSAGE),
    amounts: z.array(perMemberEntrySchema).min(1).max(SCHEDULE_MEMBER_IDS_MAX).optional(),
  })
  .strict()
  .superRefine((body, ctx) => {
    // Trùng phải so KHÔNG phân biệt hoa/thường (cùng lý do `createAdhocCollectionRequestSchema`).
    const ids = body.memberIds.map((id) => id.toLowerCase());
    const idSet = new Set(ids);
    if (idSet.size !== ids.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["memberIds"], message: MEMBER_IDS_DUPLICATE_MESSAGE });
    }
    if (body.amounts) {
      const amountIds = body.amounts.map((a) => a.groupMemberId.toLowerCase());
      const sameSet = new Set(amountIds).size === amountIds.length && amountIds.length === idSet.size && amountIds.every((id) => idSet.has(id));
      if (!sameSet) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["amounts"],
          message: "Số tiền phải có cho đúng từng người đã chọn, mỗi người một lần",
        });
      }
      // Σ các số đã sửa vẫn phải là một số tiền hợp lệ (≤ BIGINT) — nếu không response trả `sumMinor` / chênh lệch trái chính contract này.
      let sum = 0n;
      try {
        for (const a of body.amounts) sum += BigInt(a.amountMinor);
      } catch {
        return; // số lẻ hỏng đã bị `amountMinorString` báo ở từng phần tử
      }
      if (sum > AMOUNT_MODE_PG_BIGINT_MAX) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["amounts"], message: AMOUNT_MODE_INVALID_MESSAGE });
      }
    }
  });

export type SplitPreviewRequest = z.infer<typeof splitPreviewRequestSchema>;

/**
 * Σ phần đã chia so với tổng hóa đơn (`billDifference` ở `@goppy/domain` — một nguồn). Dùng ở xem trước (S3) và bảng thu của đợt theo hóa
 * đơn (S4). `under` = còn thiếu chưa chia · `over` = vượt tổng; số chênh lệch luôn > 0.
 */
export const billDifferenceSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("match") }).strict(),
  z.object({ kind: z.literal("under"), amountMinor: amountMinorString }).strict(),
  z.object({ kind: z.literal("over"), amountMinor: amountMinorString }).strict(),
]);

const splitShareSchema = z.object({ groupMemberId: memberIdField, amountMinor: amountMinorString }).strict();

export const splitPreviewResponseSchema = z
  .object({
    currency: z.string().regex(/^[A-Z]{3}$/u),
    totalMinor: amountMinorString,
    /** Theo ĐÚNG thứ tự `memberIds` client gửi. */
    shares: z.array(splitShareSchema).min(1).max(SCHEDULE_MEMBER_IDS_MAX),
    /** Chỉ khi CHIA ĐỀU và chia không hết: số đồng lẻ + người gánh (§3.5 — công khai ai gánh). Chia hết / đã sửa tay ⇒ null. */
    remainder: z.object({ amountMinor: amountMinorString, bearerMemberId: memberIdField }).strict().nullable(),
    sumMinor: amountMinorString,
    /** Σ phần từng người so với tổng hóa đơn. `under` = còn thiếu chưa chia; `over` = vượt tổng. Số chênh lệch luôn > 0. */
    difference: billDifferenceSchema,
  })
  .strict();

export type SplitPreviewResponse = z.infer<typeof splitPreviewResponseSchema>;

/* ───────────────────────── ảnh hóa đơn của đợt thu (RECEIPT-SPLIT S4) ───────────────────────── */

/** Mã lỗi ổn định của ảnh hóa đơn đính kèm đợt thu. */
export const COLLECTION_BILL_ERROR_CODES = Object.freeze({
  /** 422 — đợt không tạo theo hóa đơn (`billTotalMinor` NULL) ⇒ không có chỗ cho ảnh hóa đơn. */
  NOT_FROM_BILL: "COLLECTION_NOT_FROM_BILL",
  /** 409 — đợt đã có ảnh hóa đơn: ảnh là bằng chứng công khai cho cả nhóm, không thay âm thầm. */
  RECEIPT_ALREADY_ATTACHED: "COLLECTION_BILL_RECEIPT_ALREADY_ATTACHED",
} as const);

/**
 * US-E16.F1-1023 (@UC0464) — response `POST /v1/funds/:id/collections/:collectionId/bill-receipt`. URL đọc lại NGẮN HẠN (KEY R2 không bao giờ
 * rời API); `receiptUrlExpiresAt` null khi URL không hết hạn (public base).
 */
export const collectionBillReceiptResponseSchema = z
  .object({
    collectionId: memberIdField,
    receiptUrl: z.string().url(),
    receiptUrlExpiresAt: z.string().datetime().nullable(),
  })
  .strict();

export type CollectionBillReceiptResponse = z.infer<typeof collectionBillReceiptResponseSchema>;
